import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";

const DESKTOP_MOTION = "(min-width: 1024px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)";

export default function RouteCurtain() {
  const location = useLocation();
  const previousPath = useRef(location.pathname);
  const [scene, setScene] = useState(null);
  // Initial loads stay clear. Only desktop route changes receive a curtain.
  useEffect(() => {
    const media = window.matchMedia(DESKTOP_MOTION);
    const clearWhenDisabled = () => { if (!media.matches) setScene(null); };
    media.addEventListener("change", clearWhenDisabled);
    return () => media.removeEventListener("change", clearWhenDisabled);
  }, []);
  useEffect(() => {
    if (previousPath.current === location.pathname) return;
    previousPath.current = location.pathname;
    setScene(window.matchMedia(DESKTOP_MOTION).matches
      ? { path: location.pathname, key: location.key }
      : null);
  }, [location.pathname, location.key]);
  if (!scene) return null;
  const reduce = false;
  const mobileMotion = false;
  const path = scene.path;
  const page = path.includes("pricing")
    ? { tone:"#c86150", eyebrow:"02 / CHOOSE YOUR LEVER", title:"MAKE THE NEXT MOVE.", note:"A clear path from idea to impact.", footer:"SCOPE / VALUE / MOMENTUM", variant:"pricing", code:["PLAN / PREMIUM","VALUE > COST","CARE / ACTIVE","GROWTH / READY"] }
    : path.includes("about")
      ? { tone:"#7560c7", eyebrow:"03 / MEET THE STUDIO", title:"IDEAS TAKE SHAPE HERE.", note:"Strategy, design and systems in one room.", footer:"THINK / MAKE / IMPROVE", variant:"about", code:["STUDIO / OPEN","IDEA -> SYSTEM","CRAFT / DETAIL","SIGNAL / CLEAR"] }
      : path.includes("contact")
        ? { tone:"#d59b55", eyebrow:"04 / START THE CONVERSATION", title:"LET'S BUILD WHAT'S NEXT.", note:"Bring the ambition. We’ll shape the route.", footer:"BRIEF / ALIGN / BEGIN", variant:"contact", code:["INQUIRY / NEW","BRIEF / RECEIVED","ROUTE / READY","NEXT / CONNECT"] }
        : path.includes("services")
          ? { tone:"#286461", eyebrow:"01 / EXPLORE THE SYSTEM", title:"MAKE YOUR BUSINESS MOVE.", note:"Websites, search and connected digital systems.", footer:"DESIGN / DISCOVER / CONNECT", variant:"services", code:["SITE / RESPONSIVE","SEARCH / STRUCTURED","FORM / QUALIFIED","CARE / MONITORED"] }
          : { tone:"#286461", eyebrow:"THRIVEHAUS / DIGITAL", title:"BUILD BETTER.", note:"Get found. Thrive.", footer:"BUILD / FIND / THRIVE", variant:"home", code:["THRIVEHAUS / ONLINE","BUILD / BETTER","FOUND / CLEAR","THRIVE / NEXT"] };

  const motionState = reduce
    ? { initial:{ opacity:0 }, animate:{ opacity:0 }, style:{} }
    : mobileMotion
      ? { initial:{ opacity:.28 }, animate:{ opacity:0 }, style:{} }
      : page.variant === "services"
        ? { initial:{ scaleX:1 }, animate:{ scaleX:0 }, style:{ transformOrigin:"left center" } }
        : page.variant === "about"
          ? { initial:{ scaleY:1, rotate:0 }, animate:{ scaleY:0, rotate:-2 }, style:{ transformOrigin:"bottom center" } }
          : page.variant === "contact"
            ? { initial:{ scale:1 }, animate:{ scale:0.92, opacity:0 }, style:{ transformOrigin:"center" } }
            : { initial:{ scaleY:1 }, animate:{ scaleY:0 }, style:{ transformOrigin:"top center" } };

  return (
    <AnimatePresence mode="sync">
      <motion.div
        key={scene.key}
        className="pointer-events-none fixed inset-0 z-[65] flex flex-col justify-between overflow-hidden bg-[#0b0a08] px-6 py-7 text-white sm:px-10"
        {...motionState}
        transition={{ duration: reduce ? 0 : mobileMotion ? .18 : page.variant === "contact" ? .42 : .36, ease: [0.76, 0, 0.24, 1] }}
        aria-hidden="true"
        data-route-curtain
        onAnimationComplete={() => setScene(current => current?.key === scene.key ? null : current)}
      >
        <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[.22em] text-white/55">
          <span>{page.eyebrow}</span>
          <span style={{ color: page.tone }}>{path === "/" ? "HOME" : path.slice(1).replaceAll("/", " / ").toUpperCase()}</span>
        </div>
        <div className="relative mx-auto w-full max-w-[980px]">
          <motion.div
            initial={reduce ? false : { scaleX:0 }}
            animate={reduce ? {} : { scaleX:1 }}
            transition={{ duration:.25, delay:.02, ease:[0.76,0,0.24,1] }}
            className="h-px origin-left bg-white/25"
          />
          <div className="mt-5 flex items-end justify-between gap-8">
            <div>
              <p className="font-mono text-[10px] tracking-[.2em]" style={{color:page.tone}}>{page.note}</p>
              <p className="mt-5 max-w-[760px] font-display text-[clamp(2.8rem,8vw,7rem)] leading-[.82] tracking-[-.08em]">{page.title}</p>
            </div>
            <span className="mb-1 hidden font-mono text-[10px] tracking-[.16em] text-white/55 sm:block">SCENE / {page.variant.toUpperCase()}</span>
          </div>
          <motion.div
            initial={reduce ? false : { scaleX:0 }}
            animate={reduce ? {} : { scaleX:1 }}
            transition={{ duration:.22, delay:.04, ease:"easeOut" }}
            className="mt-8 h-1 origin-left"
            style={{ background:page.tone }}
          />
          <div className="mt-8 hidden overflow-hidden rounded border border-white/15 bg-white/[.035] p-4 sm:block">
            <div className="mb-3 flex items-center justify-between font-mono text-[8px] tracking-[.2em] text-white/35"><span>LIVE SYSTEM / TH DIGITAL</span><span style={{color:page.tone}}>● ONLINE</span></div>
            <div className="grid gap-1 font-mono text-[10px] tracking-[.12em] text-white/55 sm:grid-cols-2">
              {page.code.map((line,index)=><motion.div key={line} initial={reduce?false:{opacity:0,x:-18}} animate={{opacity:1,x:0}} transition={{delay:reduce?0:.04+index*.025}}><span className="mr-2" style={{color:page.tone}}>0{index+1}</span>{line}</motion.div>)}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-white/15 pt-4 font-mono text-[9px] tracking-[.18em] text-white/45">
          <span>{page.footer}</span>
          <span>{page.variant === "contact" ? "OPEN CHANNEL" : "SCENE CHANGE"}</span>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

